import React from 'react'
import { Link } from 'react-router-dom'
import '../styles/LandingPage.css'

export const LandingPage: React.FC = () => {
  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="landing-nav">
        <div className="nav-logo">
          <span className="logo-icon">✍️</span>
          <span className="logo-text">Australian Scholarship Writing Assistant</span>
        </div>
        <div className="nav-actions">
          <Link to="/pricing" className="nav-link">Pricing</Link>
          <Link to="/help" className="nav-link">Help</Link>
          <Link to="/login" className="nav-link">Sign In</Link>
          <Link to="/signup" className="nav-btn-primary">Get Started</Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">
            Empower Your Child's
            <span className="gradient-text"> Writing Journey</span>
          </h1>
          <p className="hero-subtitle">
            AI-powered writing assessment and personalised feedback for Australian scholarship exams (EDUTEST & ACER) and general writing development. Help your child build strong writing skills with clear, supportive, and tailored guidance.
          </p>
          <div className="hero-actions">
            <Link to="/signup" className="btn-hero-primary">
              Start Free Trial
              <span className="btn-arrow">→</span>
            </Link>
            <a href="#features" className="btn-hero-secondary">
              Learn More
            </a>
          </div>
          <div className="hero-stats">
            <div className="stat-item">
              <span className="stat-number">6</span>
              <span className="stat-label">Assessment Criteria</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">Years 3-8</span>
              <span className="stat-label">Grade Levels</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">AI</span>
              <span className="stat-label">Powered Feedback</span>
            </div>
          </div>
        </div>
        <div className="hero-visual">
          <div className="hero-card">
            <div className="card-header">
              <span className="card-dot red"></span>
              <span className="card-dot yellow"></span>
              <span className="card-dot green"></span>
            </div>
            <div className="card-content">
              <div className="sample-prompt">
                <span className="prompt-label">Writing Prompt</span>
                <p>"Describe a time when you overcame a challenge..."</p>
              </div>
              <div className="sample-scores">
                <div className="score-item">
                  <span>Creativity</span>
                  <div className="score-bar"><div className="score-fill" style={{width: '85%'}}></div></div>
                </div>
                <div className="score-item">
                  <span>Construction</span>
                  <div className="score-bar"><div className="score-fill" style={{width: '78%'}}></div></div>
                </div>
                <div className="score-item">
                  <span>Grammar</span>
                  <div className="score-bar"><div className="score-fill" style={{width: '92%'}}></div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="section-header">
          <h2>Everything You Need for Writing Success</h2>
          <p>Comprehensive tools designed to help Australian students excel in EDUTEST and ACER scholarship exams</p>
        </div>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">📝</div>
            <h3>EDUTEST & ACER Prompts</h3>
            <p>Carefully crafted writing prompts tailored for each year level, from Year 3 to Year 8, matching Australian scholarship exam standards.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🎯</div>
            <h3>6-Criteria Assessment</h3>
            <p>Detailed evaluation across Creativity, Construction, Grammar, Punctuation, Spelling, and Relevance to Task.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🤖</div>
            <h3>AI-Powered Feedback</h3>
            <p>Instant, personalised feedback powered by advanced AI to help identify strengths and areas for improvement.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">📊</div>
            <h3>Progress Tracking</h3>
            <p>Monitor improvement over time with detailed analytics and progress reports for each student.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">📚</div>
            <h3>Example Writings</h3>
            <p>Learn from annotated example writings at different quality levels to understand what makes great writing.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">👨‍👩‍👧</div>
            <h3>Parent Dashboard</h3>
            <p>Easy-to-use dashboard for parents to manage multiple student profiles and track their progress.</p>
          </div>
        </div>
      </section>


      {/* How It Works Section */}
      <section className="how-it-works-section">
        <div className="section-header">
          <h2>How It Works</h2>
          <p>Get started in three simple steps</p>
        </div>
        <div className="steps-container">
          <div className="step-card">
            <div className="step-number">1</div>
            <h3>Create Student Profiles</h3>
            <p>Set up profiles for each of your children with their year level and learning goals.</p>
          </div>
          <div className="step-connector"></div>
          <div className="step-card">
            <div className="step-number">2</div>
            <h3>Choose a Writing Prompt</h3>
            <p>Select from our library of EDUTEST and ACER prompts designed for Australian scholarship preparation.</p>
          </div>
          <div className="step-connector"></div>
          <div className="step-card">
            <div className="step-number">3</div>
            <h3>Get Instant Feedback</h3>
            <p>Receive detailed AI-powered assessment with actionable suggestions for improvement.</p>
          </div>
        </div>
      </section>

      {/* Pricing Preview Section */}
      <section className="pricing-preview-section">
        <div className="section-header">
          <h2>Simple, Transparent Pricing</h2>
          <p>Pay only when you need feedback. No subscriptions. No hidden fees.</p>
        </div>
        <div className="pricing-preview-grid">
          <div className="pricing-preview-card pricing-preview-card--free">
            <h3>Free Trial</h3>
            <div className="preview-price">1 Writing – Free</div>
            <p>Try one complete feedback report with no cost.</p>
            <Link to="/signup" className="preview-cta">Start Free</Link>
          </div>
          <div className="pricing-preview-card pricing-preview-card--popular">
            <span className="preview-badge">⭐ Most Popular</span>
            <h3>5 Writings</h3>
            <div className="preview-price">$20</div>
            <p>$4 per writing. Best value for most families.</p>
            <Link to="/pricing" className="preview-cta">View Details</Link>
          </div>
          <div className="pricing-preview-card">
            <h3>15 Writings</h3>
            <div className="preview-price">$50</div>
            <p>$3.33 per writing. Best for committed learners.</p>
            <Link to="/pricing" className="preview-cta">View Details</Link>
          </div>
        </div>
        <div className="pricing-preview-footer">
          <p>Your credits never expire. No automatic renewal.</p>
          <Link to="/pricing" className="view-all-pricing">View All Pricing Options →</Link>
        </div>
      </section>

      {/* Testimonial/CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2>Ready to Transform Your Child's Writing?</h2>
          <p>Join Australian parents helping their children prepare for scholarship exams with confidence.</p>
          <Link to="/signup" className="btn-cta">
            Get Started Today
            <span className="btn-arrow">→</span>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <span className="logo-icon">✍️</span>
            <span className="logo-text">Australian Scholarship Writing Assistant</span>
          </div>
          <div className="footer-links">
            <Link to="/login">Sign In</Link>
            <Link to="/signup">Sign Up</Link>
            <Link to="/pricing">Pricing</Link>
            <Link to="/help">Help</Link>
            <a href="#features">Features</a>
          </div>
          <div className="footer-copyright">
            © {new Date().getFullYear()} Australian Scholarship Writing Assistant. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  )
}
